import { useState } from "react";
import { challengeFamily, discountPrice, familySort } from "../lib/format";
import { SectionLabel } from "./Reveal";

export function ModelDropdowns({ challenges, family, setFamily, plan, setPlan, idPrefix }) {
  const models = [...new Set(challenges.map((c) => c.model).filter(Boolean))];
  if (models.length <= 2) return null;
  const families = ["All", ...[...new Set(models.map(challengeFamily))].sort(familySort)];
  const familyCount = (f) => challenges.filter((c) => challengeFamily(c.model) === f).length;
  const plansInFamily = family === "All" ? [] : models.filter((m) => challengeFamily(m) === family);
  const selectCls =
    "mt-1.5 rounded-xl border border-line bg-ink px-4 py-2.5 font-mono text-[11px] text-white outline-none transition-colors focus:border-signal disabled:opacity-40";
  const labelCls = "font-mono text-[9px] uppercase tracking-[0.25em] text-muted";
  return (
    <div className="flex flex-wrap items-end gap-3" data-testid={`${idPrefix}-model-filters`}>
      <label>
        <span className={labelCls}>Type</span>
        <select
          value={family}
          onChange={(e) => { setFamily(e.target.value); setPlan("All"); }}
          data-testid={`${idPrefix}-family-select`}
          className={`${selectCls} ml-0 block`}
        >
          {families.map((f) => (
            <option key={f} value={f}>{f === "All" ? `All types (${challenges.length})` : `${f} (${familyCount(f)})`}</option>
          ))}
        </select>
      </label>
      <label>
        <span className={labelCls}>Plan</span>
        <select
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          disabled={family === "All"}
          data-testid={`${idPrefix}-plan-select`}
          className={`${selectCls} block`}
        >
          <option value="All">{family === "All" ? "Pick a type first" : `All ${family} plans`}</option>
          {plansInFamily.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </label>
    </div>
  );
}

export default function ChallengeTable({ firm, label = "Challenge Accounts", testid = "firm-challenges" }) {
  const [family, setFamily] = useState("All");
  const [plan, setPlan] = useState("All");
  const challenges = firm.challenges || [];
  const models = [...new Set(challenges.map((c) => c.model).filter(Boolean))];
  const rows = challenges.filter(
    (c) => (family === "All" || challengeFamily(c.model) === family) && (plan === "All" || c.model === plan)
  );
  const hasBw = challenges.some((c) => c.price_bw);
  const anyDiscount = challenges.some((c) => discountPrice(c.price, c.discount_label || firm.discount_label));

  const priceCell = (price, label) => {
    const disc = discountPrice(price, label || firm.discount_label);
    return disc ? (
      <span>
        <span className="mr-2 text-muted/70 line-through">{price}</span>
        <span className="font-700 text-profit">{disc}</span>
      </span>
    ) : (
      <span className="text-signal">{price}</span>
    );
  };

  return (
    <div className="mt-10" data-testid={testid}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <SectionLabel>{label}</SectionLabel>
        <ModelDropdowns challenges={challenges} family={family} setFamily={setFamily} plan={plan} setPlan={setPlan} idPrefix="challenge" />
      </div>
      <div className="mt-4 overflow-x-auto rounded-2xl border border-line">
        <table className={`w-full border-collapse font-mono text-xs ${hasBw ? "min-w-[780px]" : "min-w-[620px]"}`}>
          <thead>
            <tr className="border-b border-line bg-elevate text-left text-[10px] uppercase tracking-[0.2em] text-muted">
              <th className="px-5 py-3.5 font-500">Account</th>
              {models.length > 1 && <th className="px-5 py-3.5 font-500">Model</th>}
              <th className="px-5 py-3.5 font-500">{hasBw ? "Price · On-Demand" : "Price"}</th>
              {hasBw && <th className="px-5 py-3.5 font-500">Price · Bi-Weekly</th>}
              <th className="px-5 py-3.5 font-500">Target</th>
              <th className="px-5 py-3.5 font-500">Daily DD</th>
              <th className="px-5 py-3.5 font-500">Max DD</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c, i) => (
              <tr key={`${c.size}-${c.model}-${i}`} className="border-b border-line/50 text-muted hover:bg-signal/[0.04]" data-testid={`firm-challenge-${i}`}>
                <td className="px-5 py-3.5 font-700 text-white">{c.size}</td>
                {models.length > 1 && (
                  <td className="px-5 py-3.5">
                    <span className="rounded-full border border-line bg-ink px-2.5 py-1 text-[9px] uppercase tracking-[0.12em] text-signal">{c.model || "—"}</span>
                  </td>
                )}
                <td className="px-5 py-3.5">{priceCell(c.price, c.discount_label)}</td>
                {hasBw && <td className="px-5 py-3.5">{c.price_bw ? priceCell(c.price_bw, c.discount_label) : "—"}</td>}
                <td className="px-5 py-3.5">{c.target}</td>
                <td className="px-5 py-3.5">{c.daily_dd}</td>
                <td className="px-5 py-3.5">{c.max_dd}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasBw && (
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-muted" data-testid="challenge-cycle-note">
          On-demand and bi-weekly columns are the two payout-cycle price points — on-demand lets you request rewards any time.
        </p>
      )}
      {anyDiscount && firm.discount_code && (
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-muted" data-testid="challenge-discount-note">
          Struck prices are firm list prices — club code <span className="font-700 text-signal">{firm.discount_code}</span> ({firm.discount_label}) applies at checkout.
        </p>
      )}
    </div>
  );
}
